import { useCallback, useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { CHAT_BOT } from '__queries/query-keys';
import MachineLearningService from 'services/MachineLearningService';
import { ChatSessionHistoryItem, ChatSessionHistoryResponse } from '../types';

export const useSessionHistory = (taskType: string) => {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery<ChatSessionHistoryResponse>({
    queryKey: CHAT_BOT.SESSION_HISTORIES_INFINITE(taskType),
    queryFn: async ({ pageParam }) => {
      return MachineLearningService.getChatSessionHistories(
        taskType,
        pageParam as number
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage?.next
        ? new URL(lastPage.next).searchParams.get('page')
        : undefined;
    },
    enabled: !!taskType,
  });

  const sessions: ChatSessionHistoryItem[] = useMemo(() => {
    return data?.pages.flatMap((page) => page.results) ?? [];
  }, [data]);

  const latestSession = useMemo(() => {
    return sessions.length > 0 ? sessions[0] : null;
  }, [sessions]);

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      return MachineLearningService.cancelChatSession(sessionId);
    },
    onMutate: async (sessionId: string) => {
      await queryClient.cancelQueries({
        queryKey: CHAT_BOT.SESSION_HISTORIES_INFINITE(taskType),
      });

      const previousData = queryClient.getQueryData(
        CHAT_BOT.SESSION_HISTORIES_INFINITE(taskType)
      );

      queryClient.setQueryData(
        CHAT_BOT.SESSION_HISTORIES_INFINITE(taskType),
        (old: { pages: ChatSessionHistoryResponse[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.filter(
                (session) => session.id !== sessionId
              ),
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _sessionId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          CHAT_BOT.SESSION_HISTORIES_INFINITE(taskType),
          context.previousData
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: CHAT_BOT.SESSION_HISTORIES_INFINITE(taskType),
      });
    },
  });

  const deleteSession = useCallback(
    (sessionId: string) => {
      return deleteSessionMutation.mutateAsync(sessionId);
    },
    [deleteSessionMutation]
  );

  return {
    sessions,
    latestSession,
    isLoading,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    deleteSession,
    isDeleting: deleteSessionMutation.isPending,
    refetch,
  };
};
